import config from 'config';

import { FC, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// Redux
import { useAppSelector } from 'hooks/useStore';
// Selectors
import { selectQuiz } from 'store/quiz/quizzesSelectors';
// Types
import QuizTypes from 'types/QuizTypes';
import MediaFormats from 'types/MediaFormats';
// Components
import ImageUpload from 'components/ImageUpload';
import { Input, Toggle } from 'components/Controls';
import Copy from 'components/Copy';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
import { ColorPicker } from 'components/ColorPicker';
// Utilities
import { isRequired } from 'utilities/validation';
import { scoringQuizTypes } from 'utilities/quizTypeDefaults';
// i18next
import { useTranslation } from 'react-i18next';

const PublicLeaderboard:FC = () => {
  const { t } = useTranslation();

  const quiz = useAppSelector(selectQuiz);

  const { control, watch, formState: { errors } } = useFormContext();

  const quizType = watch('type');
  const publicLeaderboardEnabled = watch('publicLeaderboard.enabled');
  const publicTeamLeaderboardEnabled = watch('publicLeaderboard.teamEnabled');

  if (!quiz || quizType === QuizTypes.Personality) return null;

  const isPollQuizzes = quiz && [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quiz.type);
  const isTeamSelectorQuiz = quiz && quiz.type === QuizTypes.TeamSelector;
  const isScoringQuizzes = quiz && scoringQuizTypes.includes(quiz.type);

  const url = `${config.quizURL}/quiz/${quiz.quizNameSlug}/${isPollQuizzes ? 'live-results' : isTeamSelectorQuiz ? 'live-teamboard' : 'live-leaderboard'}`
  const teamUrl = `${url}?scope=team`;

  return (
    <div className="mt-8">
      <div className="flex items-center mb-4">
        <span className="block text-white text-base font-semibold mr-3">{t('labels.showScreen')}</span>
        <Controller
          control={control} name="publicLeaderboard.enabled"
          render={({ field: { onChange, value } }) => (
            <Toggle
              id="publicLeaderboardEnabled"
              checked={value}
              onChange={onChange}
            />
          )}
        />
      </div>
      <span className="block text-gray-400 text-sm">
        {isPollQuizzes || isTeamSelectorQuiz ? t('pages.quizForm.publicLeaderboard.enabledPollHint') : t('pages.quizForm.publicLeaderboard.enabledHint')}
      </span>
      
      {publicLeaderboardEnabled && (
        <Fragment>
          <div className="pt-2 flex items-center gap-2">
            <a
              rel="noreferrer" target="_blank"
              className="text-sm text-primary hover:underline"
              href={url}
            >{url}</a>
            <Copy text={url} />
          </div>

          {!isPollQuizzes && !isTeamSelectorQuiz && (
            <div className="mt-4">
              <div className="flex items-center mb-2">
                <span className="block text-white text-base font-semibold mr-3">{t('labels.teamLeaderboard')}</span>
                <Controller
                  control={control} name="publicLeaderboard.teamEnabled"
                  render={({ field: { onChange, value } }) => (
                    <Toggle
                      id="publicLeaderboardTeamEnabled"
                      checked={value}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
              <span className="block text-gray-400 text-sm">{t('pages.quizForm.publicLeaderboard.teamEnabledHint')}</span>
              {publicTeamLeaderboardEnabled && (
                <div className="pt-2 flex items-center gap-2">
                  <a
                    rel="noreferrer" target="_blank"
                    className="text-sm text-primary hover:underline"
                    href={teamUrl}
                  >{teamUrl}</a>
                  <Copy text={teamUrl} />
                </div>
              )}
            </div>
          )}

          <div className="pt-4">
            <MultiLanguageTextField
              name="publicLeaderboard.headerText"
              label={t('pages.quizForm.publicLeaderboard.headerTextLabel')}
              helperText={isPollQuizzes || isTeamSelectorQuiz ? t('pages.quizForm.publicLeaderboard.headerTextPollHint') : t('pages.quizForm.publicLeaderboard.headerTextHint')}
              className="mb-4"
            />
          </div>
          <div className="w-full flex flex-wrap gap-4">
            <div className="mb-4">
              <Controller
                control={control} name="publicLeaderboard.portraitWallpaperImage"
                render={({ field }) => (
                  <ImageUpload
                    {...field}
                    format={MediaFormats.PortraitWallpaper}
                    label="labels.portraitWallpaperImage"
                  />
                )}
              />
            </div>
            <div className="flex-grow">
              <Controller
                control={control} name="publicLeaderboard.wallpaperImage"
                render={({ field }) => (
                  <ImageUpload
                    {...field}
                    format={MediaFormats.Wallpaper}
                    label="labels.wallpaperImage"
                  />
                )}
              />
            </div>
          </div>
          <div className="mb-4 flex flex-wrap gap-y-4">
            <div className="w-full sm:w-1/2 lg:w-1/3">
              <Controller
                control={control} name="publicLeaderboard.primaryColor"
                rules={{ required: isRequired }}
                render={({ field: { value, onChange } }) => (
                  <ColorPicker
                    label={t('labels.primaryColor')}
                    value={value}
                    onChange={onChange}
                    required={true}
                    error={Boolean(errors.publicLeaderboard?.primaryColor)}
                    errorText={errors.publicLeaderboard?.primaryColor ? errors.publicLeaderboard.primaryColor.message : ''}
                    helperText={isPollQuizzes ? '' : isTeamSelectorQuiz ? t('pages.quizForm.publicLeaderboard.teamSelectorPrimaryColorHint') : t('pages.quizForm.publicLeaderboard.primaryColorHint')}
                  />
                )}
              />
            </div>
            {!isTeamSelectorQuiz ? (
              <Fragment>
                <div className="w-full sm:w-1/2 lg:w-1/3">
                  <Controller
                    control={control} name="publicLeaderboard.secondaryColor"
                    rules={{ required: isRequired }}
                    render={({ field: { value, onChange } }) => (
                      <ColorPicker
                        label={t('labels.secondaryColor')}
                        value={value}
                        onChange={onChange}
                        required={true}
                        error={Boolean(errors.publicLeaderboard?.secondaryColor)}
                        errorText={errors.publicLeaderboard?.secondaryColor ? errors.publicLeaderboard.secondaryColor.message : ''}
                        helperText={isPollQuizzes ? '' : t('pages.quizForm.publicLeaderboard.secondaryColorHint')}
                      />
                    )}
                  />
                </div>
                <div className="w-full sm:w-1/2 lg:w-1/3">
                  <div className="mb-4">
                    <Controller
                      control={control} name="publicLeaderboard.textColor"
                      rules={{ required: isRequired }}
                      render={({ field: { value, onChange } }) => (
                        <ColorPicker
                          label={t('labels.textColor')}
                          value={value}
                          onChange={onChange}
                          required={true}
                          error={Boolean(errors.publicLeaderboard?.textColor)}
                          errorText={errors.publicLeaderboard?.textColor ? errors.publicLeaderboard.textColor.message : ''}
                          helperText={isPollQuizzes ? '' : t('pages.quizForm.publicLeaderboard.textColorHint')}
                        />
                      )}
                    />
                  </div>
                </div>
              </Fragment>
            ) : null}
          </div>
          {isPollQuizzes ? (
            <div className="mb-4">
              <span className="block text-white text-xs font-light">{t('pages.quizForm.publicLeaderboard.pollColorsHint')}</span>
              <span className="block text-white text-xs font-light">{t('pages.quizForm.publicLeaderboard.textColorPollHint')}</span>
            </div>
          ) : null}
          {(isScoringQuizzes || isPollQuizzes) && (
            <div className="mb-4 flex flex-wrap gap-y-4">
              <div className="w-full sm:w-1/2 lg:w-1/3">
                <Controller
                  control={control} name="publicLeaderboard.backgroundColor"
                  rules={{ required: isScoringQuizzes ? isRequired : false }}
                  render={({ field: { value, onChange } }) => (
                    <ColorPicker
                      label={t('labels.backgroundColor')}
                      value={value}
                      onChange={onChange}
                      required={isScoringQuizzes}
                      error={Boolean(errors.publicLeaderboard?.backgroundColor)}
                      errorText={errors.publicLeaderboard?.backgroundColor ? errors.publicLeaderboard.backgroundColor.message : ''}
                      helperText={isPollQuizzes ? t('pages.quizForm.publicLeaderboard.backgroundColorPollHint') : t('pages.quizForm.publicLeaderboard.backgroundColorHint')}
                    />
                  )}
                />
              </div>
            </div>
          )}
          {isPollQuizzes ? (
            <Fragment>
              <Controller
                control={control} name="publicLeaderboard.questionsTimeout"
                render={({ field: { onChange, value } }) => (
                  <Input
                    label={t('pages.quizForm.publicLeaderboard.questionsTimeoutLabel')} id="questionsTimeout" type="number" name="questionsTimeout"
                    value={value}
                    onChange={onChange}
                    className="sm:w-1/2 mb-2"
                  />
                )}
              />
              <span className="block text-gray-400 text-sm font-light leading-5 mb-4">
                {t('pages.quizForm.publicLeaderboard.questionsTimeoutHint')}
              </span>
            </Fragment>
          ) : null}
        </Fragment>
      )}
    </div>
  )
}

export default PublicLeaderboard;
